import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { GotchiDashCelebration } from '@components/Survey/Prediction/GotchiDashCelebration'
import { GotchiRainCelebration } from '@components/Survey/Prediction/GotchiRainCelebration'
import {
  GotchiWaddleCelebration,
  type FireworkOrigin,
} from '@components/Survey/Prediction/GotchiWaddleCelebration'

type GotchiCelebrationApi = {
  /** Home « Débuter » — dash L→R (~3s), does not block navigation. */
  playDash: () => void
  /** Consent « Commencer » — rain (~1.8s), does not block navigation. */
  playRain: () => void
  /** Prediction « Oui » — firework from click point, does not block navigation. */
  playBouquet: (origin?: FireworkOrigin) => void
}

const GotchiCelebrationContext = createContext<GotchiCelebrationApi | null>(null)

export function GotchiCelebrationProvider({ children }: { children: ReactNode }) {
  const [dash, setDash] = useState({ active: false, key: 0 })
  const [rain, setRain] = useState({ active: false, key: 0 })
  const [bouquet, setBouquet] = useState<{
    active: boolean
    key: number
    origin: FireworkOrigin | null
  }>({ active: false, key: 0, origin: null })

  const playDash = useCallback(() => {
    setDash((s) => ({ active: true, key: s.key + 1 }))
  }, [])

  const playRain = useCallback(() => {
    setRain((s) => ({ active: true, key: s.key + 1 }))
  }, [])

  const playBouquet = useCallback((origin?: FireworkOrigin) => {
    setBouquet((s) => ({
      active: true,
      key: s.key + 1,
      origin: origin ?? null,
    }))
  }, [])

  const api = useMemo(
    () => ({ playDash, playRain, playBouquet }),
    [playDash, playRain, playBouquet],
  )

  return (
    <GotchiCelebrationContext.Provider value={api}>
      {children}
      <GotchiDashCelebration
        key={`dash-${dash.key}`}
        active={dash.active}
        onDone={() => setDash((s) => ({ ...s, active: false }))}
      />
      <GotchiRainCelebration
        key={`rain-${rain.key}`}
        active={rain.active}
        onDone={() => setRain((s) => ({ ...s, active: false }))}
      />
      <GotchiWaddleCelebration
        key={`bouquet-${bouquet.key}`}
        active={bouquet.active}
        origin={bouquet.origin}
        onDone={() => setBouquet((s) => ({ ...s, active: false }))}
      />
    </GotchiCelebrationContext.Provider>
  )
}

export function useGotchiCelebration(): GotchiCelebrationApi {
  const ctx = useContext(GotchiCelebrationContext)
  if (!ctx) {
    return {
      playDash: () => undefined,
      playRain: () => undefined,
      playBouquet: () => undefined,
    }
  }
  return ctx
}
